import { AfterContentChecked, ChangeDetectorRef, Component, OnInit, Input, OnDestroy, AfterViewInit } from '@angular/core';
import {FormGroup, FormArray, FormControl, AbstractControl, Validators, ValidatorFn} from '@angular/forms';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DenunciaService } from 'src/app/service/denuncia/denuncia.service';
import { AuthService } from 'src/app/service/autenticacao/auth.service';

type PrimitiveValue = string | number | boolean | null | undefined;

interface ListaOpcao {
  value: PrimitiveValue;
  nome: PrimitiveValue;
  sigla?: PrimitiveValue;
  [key: string]: unknown;
}

@Component({
  selector: 'assedio',
  templateUrl: './assedio.component.html',
  styleUrls: ['./assedio.component.css']
})
export class AssedioComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {

  @Input() formGroup!: FormGroup;
  @Input() readonly: boolean = false;
  @Input() edicao: boolean = false;
  @Input() formSubmitted: boolean = false;

  areas: ListaOpcao[] = [];
  areasRadio: { val: PrimitiveValue; label: PrimitiveValue }[] = [];
  // opções para o radio de instância
  arrInstancia: { val: string; label: string }[] = [
    { val: '1', label: `1ª Instância` },
    { val: '2', label: `2ª Instância` }
  ];
  condutas: Array<{ val: PrimitiveValue; label: PrimitiveValue }> = [];
  setores: ListaOpcao[] = [];
  setorporComarca: ListaOpcao[] = [{ value: '', nome: 'Selecione' }];
  funcoes: ListaOpcao[] = [];
  escolaridades: ListaOpcao[] = [];
  tipoDeficiencias: ListaOpcao[] = [];
  corRaca: ListaOpcao[] = [];
  perguntas: any[] = [];
  comarcas: ListaOpcao[] = [];

  // flags para tornar campos apenas leitura no template durante edição
  lockNoticianteNomeInEdit: boolean = false;
  lockNoticianteEmailInEdit: boolean = false;

  private areaSub: Subscription | null = null;
  private instanciaSub: Subscription | null = null;
  private comarcasSub: Subscription | null = null;
  private comarcaSelectSub: Subscription | null = null;
  private setorBackendSub: Subscription | null = null;
  private possuiContatoSub: Subscription | null = null;
  private participanteOutroSubs: Subscription[] = [];
  private setorBackendCarregado: boolean = false;
  private setorBackendValor: PrimitiveValue = null;
  // subscriptions para observadores de perguntas dinâmicas
  private perguntaSubs: { [key: string]: Subscription | null } = {};
  private perguntaPrevVals: { [key: string]: unknown } = {};

  // subscription para alternância do modo anônimo
  private anonimoSub: Subscription | null = null;

    // flag para garantir que o aceite auto-aplicado ocorra apenas uma vez após a visualização inicial
  private _aceiteAutoApplied: boolean = false;

  // mapa local para seleção visível por pergunta: pode ser primitivo (radio) ou array (checkbox)
  selectedOptions: { [perguntaId: string]: any } = {};

  // Enum simples para representar instâncias usadas neste componente
  private enumInstancia = {
    PRIMEIRA: '1',
    SEGUNDA: '2'
  } as const;

  constructor(
    private denunciaService: DenunciaService,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // organizar inicialização em responsabilidades pequenas para facilitar leitura e testes
    this.garantirGrupoDenunciaExiste();

    this.carregarAreas();
    this.carregarCondutas();
    this.carregarListasParticipantes();

    // manter compatibilidade com normalizações e carregamento de perguntas
    this.normalizarControleInstancia();
    this.carregarPerguntas();

    this.aplicarValidatorsAssedio()
    // subscrições e watchers (centralizadas)
    this.iniciarSubscricoes();

    // preenchimentos/valores padrão
    this.preencherNoticianteComUsuario();
    this.definirNacionalidadePadrao();
  }

  private aplicarValidatorsAssedio(): void {
    const assedioGroup = this.formGroup.get('denunciaAssedio');
    if (assedioGroup) {
      // Campos raiz da denúncia de assédio
      this.aplicarValidators(assedioGroup.get('instancia'), [Validators.required]);
      this.aplicarValidators(assedioGroup.get('aceiteTermo'), [Validators.required]);
      this.aplicarValidators(assedioGroup.get('condutas'), [Validators.required]);

      // Participantes
      const participantes = assedioGroup.get('participantes') as FormArray;
      if (participantes && participantes.length > 0) {
        // VITIMA (índice 0)
        const vitima = participantes.at(0) as FormGroup;
        if (vitima) {
          if(!this.formGroup.get('anonimo').value){
            this.aplicarValidators(vitima.get('nome'), [Validators.required]);
            this.aplicarValidators(vitima.get('idade'), [Validators.required]);
            this.aplicarValidators(vitima.get('escolaridadeId'), [Validators.required]);
            this.aplicarValidators(vitima.get('nacionalidade'), [Validators.required]);
            this.aplicarValidators(vitima.get('funcaoId'), [Validators.required]);
            this.aplicarValidators(vitima.get('corRacaId'), [Validators.required]);
            this.aplicarValidators(vitima.get('celular'), [Validators.required]);
            this.aplicarValidators(vitima.get('email'), [Validators.required]);
          }
          this.aplicarValidators(vitima.get('sexo'), [Validators.required]);

        }

        // NOTICIADO (índice 1)
        if (participantes.length > 1) {
          const noticiado = participantes.at(1) as FormGroup;
          if (noticiado) {
            this.aplicarValidators(noticiado.get('nome'), [Validators.required]);
            this.aplicarValidators(noticiado.get('sexo'), [Validators.required]);
            this.aplicarValidators(noticiado.get('possuiContato'), [Validators.required]);
          }
        }
      }
    }
  }

  private removerValidatorsAssedio(){
    const assedioGroup = this.formGroup.get('denunciaAssedio');
    if (assedioGroup) {
      // Campos raiz da denúncia de assédio
      this.removerValidators(assedioGroup.get('instancia'));
      this.removerValidators(assedioGroup.get('aceiteTermo'));
      this.removerValidators(assedioGroup.get('condutas'));

      // Participantes
      const participantes = assedioGroup.get('participantes') as FormArray;
      if (participantes && participantes.length > 0) {
        // VITIMA (índice 0)
        const vitima = participantes.at(0) as FormGroup;
        if (vitima) {
          this.removerValidators(vitima.get('nome'));
          this.removerValidators(vitima.get('idade'));
          this.removerValidators(vitima.get('sexo'));
          this.removerValidators(vitima.get('escolaridadeId'));
          this.removerValidators(vitima.get('nacionalidade'));
          this.removerValidators(vitima.get('funcaoId'));
          this.removerValidators(vitima.get('corRacaId'));
          this.removerValidators(vitima.get('celular'));
          this.removerValidators(vitima.get('email'));
        }

        // NOTICIADO (índice 1)
        if (participantes.length > 1) {
          const noticiado = participantes.at(1) as FormGroup;
          if (noticiado) {
            this.removerValidators(noticiado.get('nome'));
            this.removerValidators(noticiado.get('sexo'));
            this.removerValidators(noticiado.get('possuiContato'));
          }
        }
      }
    }
  }

  private aplicarValidators(control: AbstractControl, validators: ValidatorFn[]): void {
    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private removerValidators(control: AbstractControl): void {
    control.clearValidators();
    control.updateValueAndValidity({ emitEvent: false });
  }

  private parseInstancia(val: unknown): string | null {
    if (val === null || val === undefined || val === '') return null;
    // aceitar número ou string: normalizar para string '1' ou '2'
    const s = typeof val === 'number' ? String(val) : (typeof val === 'string' ? val : String(val));
    if (s === this.enumInstancia.PRIMEIRA) return this.enumInstancia.PRIMEIRA;
    if (s === this.enumInstancia.SEGUNDA) return this.enumInstancia.SEGUNDA;
    return s; // retornar como string genérica para casos inesperados
  }

  private runDeferred(task: () => void, mode: 'micro' | 'macro' = 'macro'): void {
    if (mode === 'micro') {
      try { Promise.resolve().then(task); } catch (e) { task(); }
      return;
    }
    try { setTimeout(task, 0); } catch (e) { task(); }
  }

  private trySetControlValue(ctrl: AbstractControl | null, value: unknown, emitEvent: boolean = false): void {
    if (!ctrl) return;
    try { ctrl.setValue(value, { emitEvent: emitEvent }); } catch (e) { try { ctrl.setValue(value); } catch (ee) { /* noop */ } }
  }

  private isEmptyValue(value: any): boolean {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  }

  private getPerguntaControl(perguntaId: any): AbstractControl | null {
    return this.getControl(`denunciaAssedio.pergunta_${perguntaId}`);
  }

  private shouldShowSubmitError(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched || this.formSubmitted);
  }

  private isPerguntaObrigatoria(pergunta: any): boolean {
    return !!pergunta && pergunta.obrigatoria === 'S';
  }

  private isPerguntaVazia(pergunta: any): boolean {
    if (!pergunta) return true;
    const ctrl = this.getPerguntaControl(pergunta.id);
    const valor = ctrl ? ctrl.value : (pergunta.respostaTexto !== undefined ? pergunta.respostaTexto : pergunta.respostaChoice);
    return this.isEmptyValue(valor);
  }

  isPerguntaComErro(pergunta: any): boolean {
    return this.formSubmitted && this.isPerguntaObrigatoria(pergunta) && this.isPerguntaVazia(pergunta);
  }

  isChildPerguntaComErro(pai: any, opcao: any, child: any): boolean {
    if (!this.formSubmitted || !child || child.obrigatoria !== 'S') return false;
    if (!pai || !opcao || !this.isOptionSelected(pai, opcao)) return false;
    return this.isPerguntaVazia(child);
  }

  private getChildPerguntas(item: any): any[] {
    if (!item || !item.childPergunta) return [];
    return Array.isArray(item.childPergunta) ? item.childPergunta : [item.childPergunta];
  }

  private limparControlePerguntaFilha(ctrl: AbstractControl): void {
    const clearValue = Array.isArray(ctrl.value) ? [] : null;
    this.trySetControlValue(ctrl, clearValue, false);
    try { ctrl.clearValidators(); } catch (e) { /* noop */ }
    try { ctrl.clearAsyncValidators(); } catch (e) { /* noop */ }
    try { ctrl.updateValueAndValidity({ emitEvent: false }); } catch (e) { /* noop */ }
  }

  private limparRespostaPerguntaFilha(cp: any): void {
    const tipo = (cp && cp.tipo ? cp.tipo : '').toString().toUpperCase();
    if (tipo === 'TEXT') {
      this.setRespostaPergunta(cp.id, '', cp);
      return;
    }
    if (tipo === 'CHECKBOX' || tipo === 'CHOICE_MULTI') {
      this.setRespostaPergunta(cp.id, [], cp);
      return;
    }
    this.setRespostaPergunta(cp.id, null, cp);
  }

  private removerRespostaPerguntaDoPayload(perguntaId: any): void {
    const grpRoot = this.obterGrupoDenuncia();
    if (!grpRoot) return;
    const respostasCtrl = grpRoot.get('respostas');
    if (!respostasCtrl) return;

    const arr = respostasCtrl.value || [];
    if (!Array.isArray(arr) || !arr.length) return;

    const filtered = arr.filter((r: any) => {
      try { return String(r.perguntaId) !== String(perguntaId); } catch (e) { return true; }
    });

    if (filtered.length !== arr.length) {
      this.trySetControlValue(respostasCtrl, filtered, false);
    }
  }

  private limparCamposPerguntaFilha(cp: any): void {
    const tipo = (cp && cp.tipo ? cp.tipo : '').toString().toUpperCase();
    if (tipo === 'TEXT') {
      cp.respostaTexto = null;
      return;
    }
    if (tipo === 'RADIO' || tipo === 'CHOICE') {
      cp.respostaChoice = null;
      cp.respostaChoiceValor = null;
      return;
    }
    if (tipo === 'CHECKBOX' || tipo === 'CHOICE_MULTI') {
      cp.respostaChoice = [];
      cp.respostaChoiceValor = [];
      return;
    }
    cp.respostaTexto = null;
    cp.respostaChoice = cp.respostaChoiceValor = null;
  }

  private limparRecursivoPerguntaFilha(cp: any): void {
    if (!cp) return;
    if (cp.opcoes && Array.isArray(cp.opcoes)) {
      cp.opcoes.forEach((o: any) => { try { this.clearChildResponses(cp, o); } catch (e) { /* noop */ } });
    }
    this.getChildPerguntas(cp).forEach((d: any) => { try { this.clearChildResponses(cp, d); } catch (e) { /* noop */ } });
  }

  private processarLimpezaPerguntaFilha(cp: any, grp: FormGroup | null): void {
    if (!cp || cp.id == null) return;

    const key = `pergunta_${cp.id}`;
    const ctrl = grp ? grp.get(key) : null;
    if (ctrl) {
      try { this.limparControlePerguntaFilha(ctrl); } catch (e) { /* noop */ }
      try { this.limparRespostaPerguntaFilha(cp); } catch (e) { /* noop */ }
    }

    try { delete this.selectedOptions[`p_${cp.id}`]; } catch (e) { /* noop */ }
    try { this.removerRespostaPerguntaDoPayload(cp.id); } catch (e) { /* noop */ }
    try { this.limparCamposPerguntaFilha(cp); } catch (e) { /* noop */ }
    try { this.limparRecursivoPerguntaFilha(cp); } catch (e) { /* noop */ }
  }

  private setPerguntaObrigatoria(pergunta: any, required: boolean): void {
    if (!pergunta || pergunta.id == null) return;
    const grp = this.obterGrupoDenuncia();
    if (!grp) return;

    const ctrl = grp.get(`pergunta_${pergunta.id}`);
    if (!ctrl) return;

    try {
      if (required) {
        ctrl.setValidators([Validators.required]);
      } else {
        ctrl.clearValidators();
        ctrl.clearAsyncValidators();
      }
      ctrl.updateValueAndValidity({ emitEvent: false });
    } catch (e) { /* noop */ }
  }

  private obterValorSelecionadoPergunta(perguntaId: any): any {
    const grp = this.obterGrupoDenuncia();
    if (!grp) return this.selectedOptions[`p_${perguntaId}`];
    const ctrl = grp.get(`pergunta_${perguntaId}`);
    return ctrl ? ctrl.value : this.selectedOptions[`p_${perguntaId}`];
  }

  private aplicarObrigatoriedadeChildPerguntasVisiveis(pergunta: any): void {
    if (!pergunta || !pergunta.opcoes || !pergunta.opcoes.length) return;

    const valorAtual = this.obterValorSelecionadoPergunta(pergunta.id);
    const tipo = (pergunta.tipo || '').toString().toUpperCase();
    const opcoesSelecionadas = (tipo === 'CHECKBOX' || Array.isArray(valorAtual))
      ? (Array.isArray(valorAtual) ? valorAtual : (valorAtual ? [valorAtual] : [])).map((v: any) => String(v))
      : (valorAtual === null || valorAtual === undefined || valorAtual === '' ? [] : [String(valorAtual)]);

    pergunta.opcoes.forEach((opt: any) => {
      if (!opt || !opt.childPergunta) return;
      const valorOpcao = String(opt.val || opt.value || opt.id || opt.valor || '');
      const isSelected = opcoesSelecionadas.indexOf(valorOpcao) >= 0;
      this.toArray(opt.childPergunta).forEach((cp: any) => {
        this.setPerguntaObrigatoria(cp, isSelected && cp && cp.obrigatoria === 'S');
        if (isSelected) {
          this.aplicarObrigatoriedadeChildPerguntasVisiveis(cp);
        }
      });
    });
  }

  private sincronizarObrigatoriedadePergunta(pergunta: any): void {
    this.aplicarObrigatoriedadeChildPerguntasVisiveis(pergunta);
  }

  /** Limpa respostas e controles relacionados aos childPergunta de uma opção removida
   * percorre childPergunta recursivamente e zera valores nos controles `pergunta_<id>`.
   */
  private clearChildResponses(pergunta: any, opcao: any) {
    try {
      if (!pergunta || !opcao) return;
      const childs = this.getChildPerguntas(opcao);
      if (!childs.length) return;
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      childs.forEach((cp: any) => this.processarLimpezaPerguntaFilha(cp, grp));
    } catch (e) { /* noop */ }
  }

  private normalizePerguntaValor(v: any): any {
    if (v === undefined || v === null) return v;
    if (Array.isArray(v)) {
      return v
        .map(x => (x && typeof x === 'object')
          ? (x.val !== undefined && x.val !== null
            ? String(x.val)
            : (x.value !== undefined && x.value !== null ? String(x.value) : String(x)))
          : String(x))
        .map((s: any) => String(s));
    }
    if (typeof v === 'object') {
      const cand = v.val !== undefined && v.val !== null
        ? v.val
        : (v.value !== undefined && v.value !== null
          ? v.value
          : (v.id !== undefined && v.id !== null ? v.id : (v.valor !== undefined && v.valor !== null ? v.valor : v)));
      return String(cand);
    }
    return String(v);
  }

  private findOpcaoPorValor(opcoes: any[], valor: any): any | null {
    if (!opcoes || !opcoes.length) return null;
    return opcoes.find((o: any) => {
      try { return String(o.val || o.value || o.id || o.valor) === String(valor); } catch (e) { return false; }
    }) || null;
  }

  private limparFilhosPorRemocao(pergunta: any, removed: any[]): void {
    if (!removed || !removed.length || !pergunta || !pergunta.opcoes) return;
    removed.forEach((rv: any) => {
      const opt = this.findOpcaoPorValor(pergunta.opcoes, rv);
      if (opt) {
        try { this.clearChildResponses(pergunta, opt); } catch (e) { /* noop */ }
      }
    });
  }

  private limparFilhosPorTrocaRadio(pergunta: any, prevNorm: any): void {
    if (!pergunta || !pergunta.opcoes) return;
    const prevOpt = this.findOpcaoPorValor(pergunta.opcoes, prevNorm);
    if (prevOpt) {
      try { this.clearChildResponses(pergunta, prevOpt); } catch (e) { /* noop */ }
    }
  }

  // ---------------- helper methods used by ngOnInit (separar responsabilidades) ----------------
  private garantirGrupoDenunciaExiste(): void {
    if (this.formGroup && !this.formGroup.get('denunciaAssedio')) {
      const grupo = new FormGroup({
        instancia: new FormControl(null),
        condutas: new FormControl([]),
        area: new FormControl(null),
        setor: new FormControl(null)
      });
      (this.formGroup as FormGroup).addControl('denunciaAssedio', grupo);
    }
  }

  private carregarAreas(): void {
    this.areas = this.denunciaService.carregarAreas();
    this.areasRadio = (this.areas || []).map(a => ({ val: a.value, label: a.nome }));
  }

  private carregarCondutas(): void {
    this.denunciaService.carregarCondutas().subscribe(ret => {
      const raw = this.unwrap(ret) || [];
      this.condutas = (raw as any[]).map((item: any) => ({ val: item.id || item.codigo || item.value || item.valor, label: item.nome || item.label || item.descricao || item.valor }));
      // reaplicar valores existentes do form (se houver)
      try {
        const grp = this.obterGrupoDenuncia();
        const condCtrl = grp ? grp.get('condutas') : null;
        const existing = condCtrl ? condCtrl.value : null;
          if (Array.isArray(existing) && existing.length) {
          const selected = existing.map((c: any) => (c && c.condutaId != null) ? c.condutaId : (c && c.id != null) ? c.id : (c && c.value != null) ? c.value : c);
          this.runDeferred(() => this.trySetControlValue(condCtrl, selected, false), 'micro');
        }
      } catch (e) { /* noop */ }
    });
  }

  private carregarListasParticipantes(): void {
    forkJoin([
      this.carregarFuncoes(),
      this.carregarEscolaridades(),
      this.carregarTipoDeficiencias(),
      this.carregarCorRaca()
    ]).subscribe(() => {
      this.talvezReaplicarSelecoesParticipantes();
    });
  }

  private carregarFuncoes(): Observable<void> {
    return this.denunciaService.carregarFuncoes().pipe(map(ret => {
      const raw = this.unwrap(ret) || [];
      this.funcoes = (raw.map((f: any) => ({
        value: String(f.id || f.codigo || f.value),
        nome: String(f.nome || f.label || f.descricao)
      })) || []);
      this.funcoes.push({ value: '9999', nome: 'Outro' });
    }));
  }

  private carregarEscolaridades(): Observable<void> {
    return this.denunciaService.carregarEscolaridades().pipe(map(ret => {
      const raw = this.unwrap(ret) || [];
      this.escolaridades = raw.map((e: any) => ({
        value: String(e.id || e.codigo || e.value),
        nome: String(e.nome || e.label || e.descricao)
      }));
    }));
  }

  private carregarTipoDeficiencias(): Observable<void> {
    return this.denunciaService.carregarTipoDeficiencias().pipe(map(ret => {
      const raw = this.unwrap(ret) || [];
      this.tipoDeficiencias = (raw.map((d: any) => ({
        value: String(d.id || d.codigo || d.value),
        nome: String(d.nome || d.label || d.descricao)
      })) || []);
      this.tipoDeficiencias.push({ value: '9999', nome: 'Outro' });
    }));
  }

  private carregarCorRaca(): Observable<void> {
    return this.denunciaService.carregarCorRaca().pipe(map(ret => {
      const raw = this.unwrap(ret) || [];
      this.corRaca = raw.map((c: any) => ({
        value: String(c.id || c.codigo || c.value),
        nome: String(c.nome || c.label || c.descricao)
      }));
    }));
  }

  private talvezReaplicarSelecoesParticipantes(): void {
    const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
    const parts = grp && grp.get('participantes') as FormArray;
    if (parts && parts.length > 0) {
      this.reaplicarSelecoesParticipantes();
    }
  }

  private inscreverControleArea(): void {
    const areaCtrl = this.getControl('denunciaAssedio.area');
    if (!areaCtrl || !areaCtrl.valueChanges) return;
    // idempotente: não inscrever se já existe uma subscription ativa
    if (this.areaSub) return;
    try {
      this.areaSub = (areaCtrl.valueChanges as any).pipe(startWith(areaCtrl.value)).subscribe((val: any) => {
        // adiar a execução do handler para o próximo macrotask garantindo que o
        // change detection corrente finalize antes de alterarmos estado/validadores
        this.runDeferred(() => this.onAreaSelect(val), 'macro');
      });
    } catch (e) {
      this.areaSub = areaCtrl.valueChanges.subscribe((val: any) => {
        this.runDeferred(() => this.onAreaSelect(val), 'micro');
      });
    }
  }

  private inscreverComarcasRaiz(): void {
    if (this.formGroup && this.formGroup.root && this.formGroup.root.get('listaComarcas')) {
      const listaComCtrl = this.formGroup.root.get('listaComarcas') as AbstractControl;
      const initial = listaComCtrl && listaComCtrl.value ? listaComCtrl.value : [];
      this.comarcas = this.normalizarOpcoes(this.unwrap(initial));
      if (listaComCtrl && listaComCtrl.valueChanges) {
        this.unsubscribeSafe(this.comarcasSub);
        this.comarcasSub = listaComCtrl.valueChanges.subscribe((v: any) => {
          try { this.comarcas = this.normalizarOpcoes(this.unwrap(v || [])); } catch (e) { this.comarcas = []; }
        });
      }
    }
  }

  private configurarObservadorPossuiContato(): void {
    const grp = this.obterGrupoDenuncia();
    const parts = grp && grp.get('participantes') as FormArray;
    if (!parts || parts.length <= 1) return;
    const p1 = parts.at(1) as FormGroup;
    if (!p1) return;

    const possuiCtrl = p1.get('possuiContato') as FormControl | null;
    // manter sempre o controle contato para evitar alteração da árvore de formulários
    if (!p1.get('contato')) {
      // criar desabilitado por padrão (evita validação antes de necessário)
      p1.addControl('contato', new FormControl({ value: null, disabled: true }));
    }
    const contatoCtrl = p1.get('contato') as FormControl;

    // Determinar estado inicial com base em valores existentes (email/celular) ou no próprio possuiContato
    const existingEmail = p1.get('email') ? p1.get('email')!.value : null;
    const existingCelular = p1.get('celular') ? p1.get('celular')!.value : null;
    const hasContactValue = (existingEmail && String(existingEmail).trim() !== '') || (existingCelular && String(existingCelular).trim() !== '');

    const normalizeYes = (v: any) => (v === 'S' || v === 's' || v === true);

    const applyState = (isYes: boolean, valueToSet?: string) => {
      try {
        if (isYes) {
          contatoCtrl.enable({ emitEvent: false });
          contatoCtrl.setValidators([Validators.required]);
          if (valueToSet !== undefined && valueToSet !== null && String(valueToSet).trim() !== '') {
            contatoCtrl.setValue(String(valueToSet), { emitEvent: false });
          }
        } else {
          contatoCtrl.setValue(null, { emitEvent: false });
          contatoCtrl.clearValidators();
          contatoCtrl.disable({ emitEvent: false });
          const emailCtrl = p1.get('email');
          if (emailCtrl) {
            try { emailCtrl.setValue(null, { emitEvent: false }); } catch (e) { emailCtrl.setValue(null); }
          }
          const celularCtrl = p1.get('celular');
          if (celularCtrl) {
            try { celularCtrl.setValue(null, { emitEvent: false }); } catch (e) { celularCtrl.setValue(null); }
          }

        }
        contatoCtrl.updateValueAndValidity({ emitEvent: false });
      } catch (e) { /* noop */ }
    };

    // aplicar estado inicial no próximo macrotask para evitar ExpressionChangedAfterItHasBeenCheckedError
    this.runDeferred(() => {
      const initialYes = normalizeYes(possuiCtrl ? possuiCtrl.value : null) || hasContactValue;
      const initialValue = (existingEmail && String(existingEmail).trim() !== '') ? existingEmail : (existingCelular || null);
      // se não for readonly, e detectamos contato existente mas possuiCtrl não estiver marcado, marcar sem emitir eventos
      if (!this.readonly && hasContactValue && possuiCtrl && !normalizeYes(possuiCtrl.value)) {
        this.trySetControlValue(possuiCtrl, 'S', false);
      }
      applyState(initialYes, initialValue as any);
    }, 'macro');

    // Inscrever de forma idempotente e agendar aplicação via macrotask
    try {
      if (possuiCtrl && possuiCtrl.valueChanges) {
        this.unsubscribeSafe(this.possuiContatoSub);
        this.possuiContatoSub = possuiCtrl.valueChanges.subscribe((v: any) => {
          this.runDeferred(() => applyState(normalizeYes(v)), 'macro');
        });
      }
    } catch (e) { /* noop */ }
  }

  private aplicarObrigatoriedadeCampoOutro(participante: FormGroup, origem: string, destino: string): void {
    const origemCtrl = participante.get(origem);
    const destinoCtrl = participante.get(destino);
    if (!origemCtrl || !destinoCtrl) return;

    const aplicar = (valorOrigem: unknown, limparTextoResidual: boolean) => {
      const ehOutro = Number(valorOrigem) === 9999 || String(valorOrigem || '').toLowerCase().indexOf('outro') >= 0;
      if (ehOutro) {
        destinoCtrl.setValidators([Validators.required]);
      } else {
        destinoCtrl.clearValidators();
        // Só limpar quando houver alteração do usuário; na carga inicial,
        // preservar o texto para permitir forçar a seleção "Outro" em edição.
        if (limparTextoResidual && this.valorPreenchido(destinoCtrl.value)) {
          destinoCtrl.setValue(null, { emitEvent: false });
        }
      }
      destinoCtrl.updateValueAndValidity({ emitEvent: false });
    };

    aplicar(origemCtrl.value, false);
    const sub = origemCtrl.valueChanges.subscribe((v: unknown) => aplicar(v, true));
    this.participanteOutroSubs.push(sub);
  }

  private configurarObrigatoriedadeCamposOutroParticipantes(): void {
    const grp = this.obterGrupoDenuncia();
    const parts = grp && grp.get('participantes') as FormArray;
    if (!parts || parts.length === 0) return;

    this.participanteOutroSubs.forEach((s: Subscription) => this.unsubscribeSafe(s));
    this.participanteOutroSubs = [];

    const p0 = parts.at(0) as FormGroup;
    if (p0) {
      this.aplicarObrigatoriedadeCampoOutro(p0, 'funcaoId', 'funcaoOutro');
      this.aplicarObrigatoriedadeCampoOutro(p0, 'deficienciaId', 'deficienciaOutro');
    }

    const p1 = parts.length > 1 ? parts.at(1) as FormGroup : null;
    if (p1) {
      this.aplicarObrigatoriedadeCampoOutro(p1, 'funcaoId', 'funcaoOutro');
    }
  }

  /** Agrupa a inicialização de subscrições / watchers do componente. */
  private iniciarSubscricoes(): void {
    this.inscreverComarcasRaiz();
    // Em edição, capturar primeiro o valor de setor vindo do backend
    // para evitar limpezas indevidas no fluxo de instância.
    this.inscreverSetorBackendInicial();
    this.inscreverInstancia();
    this.iniciarComportamentoComarcaSetor();
    this.configurarObservadorPossuiContato();
    this.configurarObrigatoriedadeCamposOutroParticipantes();
    this.inscreverAnonimo();
  }

  private valorPreenchido(value: unknown): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private inscreverSetorBackendInicial(): void {
    if (!this.edicao) return;

    const setorCtrl = this.getControl('denunciaAssedio.setor');
    if (!setorCtrl) return;

    const registrar = (value: any) => {
      if (!this.valorPreenchido(value)) return;
      if (this.setorBackendCarregado) return;

      this.setorBackendCarregado = true;
      this.setorBackendValor = value;
      this.reaplicarSetorDepoisDoBackend();
      this.unsubscribeSafe(this.setorBackendSub);
    };

    registrar(setorCtrl.value);

    if (!this.setorBackendCarregado && setorCtrl.valueChanges) {
      this.unsubscribeSafe(this.setorBackendSub);
      this.setorBackendSub = setorCtrl.valueChanges.subscribe((value: any) => registrar(value));
    }
  }

  private getSetorParaReaplicar(): PrimitiveValue {
    const ctrl = this.getControl('denunciaAssedio.setor');
    const current = ctrl ? ctrl.value : null;
    return this.valorPreenchido(current) ? current : this.setorBackendValor;
  }

  private reaplicarSetorDepoisDoBackend(): void {
    const inst = this.parseInstancia(this.getControl('denunciaAssedio.instancia') ? this.getControl('denunciaAssedio.instancia')!.value : null);

    if (inst === this.enumInstancia.PRIMEIRA) {
      const comarcaCtrl = this.getControl('denunciaAssedio.comarcaMunicipioCodigo');
      const comarca = comarcaCtrl ? comarcaCtrl.value : null;
      if (this.valorPreenchido(comarca)) {
        this.carregarSetorPorComarcaEReaplicar(comarca, this.getSetorParaReaplicar(), true);
      }
      return;
    }

    if (inst === this.enumInstancia.SEGUNDA) {
      const areaCtrl = this.getControl('denunciaAssedio.area');
      const area = areaCtrl ? areaCtrl.value : null;
      if (this.valorPreenchido(area)) {
        this.onAreaSelect(area);
      }
    }
  }

  private reaplicarSetorComLista(listCandidate?: ListaOpcao[]): void {
    const ctrl = this.getControl('denunciaAssedio.setor');
    if (!ctrl) return;
    if (this.edicao && !this.setorBackendCarregado) return;

    const grp = this.obterGrupoDenuncia();
    const existing = grp && grp.get('setor') ? grp.get('setor')!.value : null;

    this.runDeferred(() => {
      if (!this.valorPreenchido(existing)) {
        this.safeSetValue(ctrl, null);
        return;
      }

      if (listCandidate && listCandidate.length) {
        const canonical = this.findCanonicalSetorValue(listCandidate, existing as PrimitiveValue);
        if (canonical) {
          this.safeSetValue(ctrl, canonical);
          return;
        }
      }

      this.safeSetValue(ctrl, existing);
    }, 'macro');
  }

  private garantirListaBaseSetorPorComarca(): void {
    if (!this.setorporComarca || !this.setorporComarca.length) {
      this.setorporComarca = [{ value: '', nome: 'Selecione' }];
    }
  }

  private limparAreaQuandoPrimeiraInstancia(): void {
    const grp = this.obterGrupoDenuncia();
    const areaCtrl = grp ? grp.get('area') : null;
    if (areaCtrl && this.valorPreenchido(areaCtrl.value)) {
      this.safeSetValue(areaCtrl, null, { emitEvent: false, reapply: false });
    }
  }

  private aplicarFluxoPrimeiraInstancia(): void {
    this.unsubscribeSafe(this.areaSub);
    this.areaSub = null;
    this.limparAreaQuandoPrimeiraInstancia();
    this.garantirListaBaseSetorPorComarca();
    this.reaplicarSetorComLista(this.setorporComarca);
  }

  private aplicarFluxoSegundaInstancia(): void {
    this.inscreverControleArea();
    this.reaplicarSetorComLista(this.setores);
  }

  /** Inscreve em mudanças do controle 'instancia' para ajustar validadores e limpar/repovoar o campo 'setor' quando necessário. */
  private inscreverInstancia(): void {
    const instCtrl = this.getControl('denunciaAssedio.instancia');
    if (!instCtrl || !instCtrl.valueChanges) return;
    if (this.instanciaSub) return;

    const handle = (raw: unknown) => {
      const instancia = this.parseInstancia(raw);
      this._toggleAreaSetorRequired(instancia);

      if (instancia === this.enumInstancia.PRIMEIRA) {
        this.aplicarFluxoPrimeiraInstancia();
        return;
      }

      if (instancia === this.enumInstancia.SEGUNDA) {
        this.aplicarFluxoSegundaInstancia();
      }
    };

    try {
      this.instanciaSub = (instCtrl.valueChanges as any).pipe(startWith(instCtrl.value)).subscribe((v: any) => {
        this.runDeferred(() => handle(v), 'macro');
      });
    } catch (e) {
      this.instanciaSub = instCtrl.valueChanges.subscribe((v: any) => { this.runDeferred(() => handle(v), 'macro'); });
    }

  }

  ngAfterViewInit(): void {

    // aplicar automaticamente o aceiteTermo depois que a view inicial for verificada
    // usar microtask após change detection inicial
    this.runDeferred(() => this.aplicarAceiteSeNecessario(), 'micro');
  }

  ngAfterContentChecked(): void {
    try { this.changeDetectorRef.detectChanges(); } catch (e) { /* noop */ }
  }

  /** Aplica o valor 'S' ao controle aceiteTermo se existirem respostas salvas e ainda não tivermos aplicado antes. */
  private aplicarAceiteSeNecessario(): void {
    if (this._aceiteAutoApplied) return;
    try {
      const grpForAceite = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      const respostas = this.formGroup && this.formGroup.value && this.formGroup.value.denunciaAssedio && this.formGroup.value.denunciaAssedio.respostas ? this.formGroup.value.denunciaAssedio.respostas : [];
      if (grpForAceite && Array.isArray(respostas) && respostas.length) {
        const aceiteCtrl = grpForAceite.get('aceiteTermo');
        if (aceiteCtrl) {
          try { aceiteCtrl.setValue('S', { emitEvent: false }); } catch (e) { /* noop */ }
        }
      }
    } catch (e) { /* noop */ }
    this._aceiteAutoApplied = true;
  }

  /**
   * Inicializa o comportamento de carregamento de setores quando a comarca muda.
   * - Carrega inicialmente caso exista valor em `denunciaAssedio.comarcaMunicipioCodigo` e reaplica
   *   o valor de `denunciaAssedio.setor` previamente presente (sem disparar eventos).
   * - Inscreve em valueChanges para trocar lista de setores quando a comarca mudar.
   */
  private iniciarComportamentoComarcaSetor() {
    const comarcaCtrl = this.getControl('denunciaAssedio.comarcaMunicipioCodigo');
    if (!comarcaCtrl) return;

    const applyDefaultEmpty = () => { this.setorporComarca = [{ value: '', nome: 'Selecione' }]; };

    // helper para carregar e aplicar setores, opcionalmente reaplicando um valor existente
    const loadAndApply = (codigo: any, existingSetorVal?: any, restoreExisting: boolean = false) => {
      if (codigo === null || codigo === undefined || codigo === '') {
        applyDefaultEmpty();
        const setorCtrl = this.getControl('denunciaAssedio.setor');
        if (setorCtrl && (!this.edicao || this.setorBackendCarregado)) setorCtrl.setValue(null);
        return;
      }
      this.carregarSetorPorComarcaEReaplicar(codigo, existingSetorVal, restoreExisting);
    };

    // carga inicial (preservando setor existente se houver)
    try {
      const initial = comarcaCtrl.value;
      const grpForCom = this.formGroup ? (this.formGroup.get('denunciaAssedio') as FormGroup) : null;
      const existingSetorVal = grpForCom ? (grpForCom.get('setor') ? grpForCom.get('setor')!.value : null) : null;
      if (initial !== null && initial !== undefined && initial !== '') {
        loadAndApply(initial, existingSetorVal, true);
      }
    } catch (e) {
      // noop - não bloquear inicialização
    }

    // subscription para mudanças subsequentes
    if (comarcaCtrl.valueChanges) {
      this.unsubscribeSafe(this.comarcaSelectSub);
      this.comarcaSelectSub = comarcaCtrl.valueChanges.subscribe((val: any) => {
        try {
          loadAndApply(val);
        } catch (e) {
          applyDefaultEmpty();
        }
      });
    }
  }

  private carregarSetorPorComarcaEReaplicar(codigo: any, existingSetorVal?: any, restoreExisting: boolean = false): void {
    this.denunciaService.carregarSetorPorComarca(codigo).subscribe(ret => {
      const normalized = this.normalizarOpcoesSetorComarca(this.unwrap(ret));
      const setorCtrl = this.getControl('denunciaAssedio.setor');
      if (!setorCtrl) return;

      const existing = this.valorPreenchido(existingSetorVal) ? existingSetorVal : this.getSetorParaReaplicar();
      const deveReaplicar = restoreExisting && this.valorPreenchido(existing) && (!this.edicao || this.setorBackendCarregado);

      if (deveReaplicar) {
        this.setorporComarca = this.garantirSetorSelecionadoNaLista(normalized, existing);
        const canonical = this.findCanonicalSetorValue(normalized, existing);
        if (canonical) {
          this.safeSetValue(setorCtrl, canonical);
        } else {
          this.safeSetValue(setorCtrl, existing);
        }
      } else {
        this.setorporComarca = normalized;
        if (!this.edicao || this.setorBackendCarregado) {
          this.safeSetValue(setorCtrl, null);
        }
      }

    }, err => {
      this.setorporComarca = [{ value: '', nome: 'Selecione' }];
    });
  }

  /** Define "Brasileira" como valor padrão para nacionalidade do participante 0 quando o controle existir */
  private definirNacionalidadePadrao(): void {

      const grp = this.obterGrupoDenuncia();
      if (!grp) return;
      const parts = grp.get('participantes') as FormArray;
      if (!parts || parts.length === 0) return;
      const p0 = parts.at(0) as FormGroup;
      if (!p0) return;

      const natCtrl = p0.get('nacionalidade');
      const defaultText = 'Brasileiro';
      if (natCtrl) {
        const val = natCtrl.value;
        if (val === null || val === undefined || String(val).trim() === '') {
          this.runDeferred(() => this.safeSetValue(natCtrl, defaultText, { emitEvent: false, reapply: false }), 'micro');
        }
      } else {
        p0.addControl('nacionalidade', new FormControl(defaultText));
      }
  }

  /** Preenche o participante 0 (noticiante) com nome e email do usuário logado quando disponível. */
  private preencherNoticianteComUsuario(): void {
    const usuario = this.authService.usuarioLogado;
    if (!usuario) return;

    const name = usuario.nome || usuario.username || '';
    const email = usuario.email || usuario.username || '';

    const applyIfNeeded = (): boolean => {
      const grp = this.obterGrupoDenuncia();
      if (!grp) return false;
      const parts = grp.get('participantes') as FormArray;
      if (!parts || parts.length === 0) return false;
      const p0 = parts.at(0) as FormGroup;
      if (!p0) return false;

      // apenas preencher se os campos estiverem vazios
      const nomeCtrl = p0.get('nome');
      const emailCtrl = p0.get('email');

      // se já houver valor salvo, manter
      const hasNome = nomeCtrl && nomeCtrl.value !== null && nomeCtrl.value !== undefined && String(nomeCtrl.value).trim() !== '';
      const hasEmail = emailCtrl && emailCtrl.value !== null && emailCtrl.value !== undefined && String(emailCtrl.value).trim() !== '';

      // se readonly, não sobrescrever valores existentes
      if (this.readonly) return true;

      // preencher nome/email somente quando ausentes
      if (!hasNome && name) {
        this.runDeferred(() => this.safeSetValue(nomeCtrl, String(name), { emitEvent: false, reapply: false }), 'micro');
      }
      if (!hasEmail && email) {
        this.runDeferred(() => this.safeSetValue(emailCtrl, String(email), { emitEvent: false, reapply: false }), 'micro');
      }

      return true;
    };

    if (!applyIfNeeded()) {
      const rootGrp = this.formGroup && this.formGroup.get('denunciaAssedio');
      if (rootGrp && rootGrp.valueChanges && rootGrp.valueChanges.subscribe) {
        const sub = rootGrp.valueChanges.subscribe(() => {
          if (applyIfNeeded()) {
            try { sub.unsubscribe(); } catch (e) { /* ignore */ }
          }
        });
      }
    }
  }

  /** Retorna o FormControl do participante no índice e campo informado. Retorna um FormControl vazio como fallback para evitar erros no template. */
  getParticipantControl(index: number, field: string): FormControl {
    try {
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      if (!grp) return new FormControl(null);
      const parts = grp.get('participantes') as FormArray;
      if (!parts || !parts.controls || !parts.controls[index]) return new FormControl(null);
      const ctrl = parts.controls[index].get(field) as FormControl;
      return ctrl || new FormControl(null);
    } catch (e) {
      return new FormControl(null);
    }
  }

  /**
   * Reaplica valores existentes dos participantes em selects que carregam opções assincronamente.
   * Compara o valor atual do controle com os itens da lista e tenta encontrar correspondência por
   * diversos campos (value, id, codigo, nome, label). Se encontrar, seta o valor padronizado.
   */
  private reaplicarSelecoesParticipantes(): void {
    const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
    if (!grp) return;
    const parts = grp.get('participantes') as FormArray;
    if (!parts || parts.length === 0) return;

    const mappings: { field: string; list: any[] }[] = [
      { field: 'escolaridadeId', list: this.escolaridades },
      { field: 'funcaoId', list: this.funcoes },
      { field: 'deficienciaId', list: this.tipoDeficiencias },
      { field: 'corRacaId', list: this.corRaca }
    ];

    const findMatchValue = (list: any[], value: any): any | null => {
      if (!list || list.length === 0 || value === null || value === undefined || value === '') return null;
      const sv = String(value).toLowerCase();
      for (const it of list) {
        if (!it) continue;
        const candidates = [it.value, it.id, it.codigo, it.val, it.valor, it.nome, it.label];
        for (const cand of candidates) {
          if (cand === undefined || cand === null) continue;
          if (String(cand).toLowerCase() === sv) return String(it.value !== undefined ? it.value : (it.id !== undefined ? it.id : cand));
        }
        const name = it.nome || it.label || it.descricao;
        if (name && String(name).toLowerCase() === sv) return String(it.value !== undefined ? it.value : (it.id !== undefined ? it.id : name));
      }
      return null;
    };

    const forcarSelecaoOutroSeTextoPreenchido = (p: FormGroup, idField: string, outroField: string, list: any[]): void => {
      const idCtrl = p.get(idField) as FormControl | null;
      const outroCtrl = p.get(outroField) as FormControl | null;
      if (!idCtrl || !outroCtrl) return;

      if (!this.valorPreenchido(outroCtrl.value)) return;

      const current = idCtrl.value;
      const currentMatch = findMatchValue(list, current);
      if (this.valorPreenchido(current) && currentMatch !== null) return;

      let valorOutro = findMatchValue(list, '9999');
      if (valorOutro === null && Array.isArray(list)) {
        const itemOutro = list.find((it: any) => {
          const nome = (it && (it.nome || it.label || it.descricao || it.valor)) || '';
          return String(nome).toLowerCase().indexOf('outro') >= 0;
        });
        if (itemOutro) {
          valorOutro = itemOutro.value !== undefined ? itemOutro.value : (itemOutro.id !== undefined ? itemOutro.id : null);
        }
      }

      if (valorOutro === null || valorOutro === undefined || valorOutro === '') {
        valorOutro = '9999';
      }

      this.safeSetValue(idCtrl, valorOutro, { emitEvent: false, reapply: true });
    };

    for (let i = 0; i < parts.length; i++) {
      const p = parts.at(i) as FormGroup;
      for (const map of mappings) {
        const ctrl = p.get(map.field) as FormControl;
        if (!ctrl) continue;
        const current = ctrl.value;
        const toSet = findMatchValue(map.list, current);
        if (toSet !== null) {
          const valorNormalizado = String(toSet);
          // setar sem emitir eventos e garantir reaplicação pós-render
          this.safeSetValue(ctrl, valorNormalizado, { emitEvent: false, reapply: true });
        }
      }

      // Em edição, se vier apenas texto em campos "Outro", selecionar explicitamente "Outro" no select.
      forcarSelecaoOutroSeTextoPreenchido(p, 'funcaoId', 'funcaoOutro', this.funcoes);
      forcarSelecaoOutroSeTextoPreenchido(p, 'deficienciaId', 'deficienciaOutro', this.tipoDeficiencias);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeSafe(this.areaSub);
    this.unsubscribeSafe(this.instanciaSub);
    this.unsubscribeSafe(this.comarcasSub);
    this.unsubscribeSafe(this.comarcaSelectSub);
    this.unsubscribeSafe(this.setorBackendSub);
    this.unsubscribeSafe(this.possuiContatoSub);
    this.unsubscribeSafe(this.anonimoSub);
    this.participanteOutroSubs.forEach((s: Subscription) => this.unsubscribeSafe(s));
    this.participanteOutroSubs = [];
    // limpar observadores de perguntas dinâmicas
    this.removerValidatorsAssedio();
    try {
      if (this.perguntaSubs) {
        Object.keys(this.perguntaSubs).forEach(k => { try { this.unsubscribeSafe(this.perguntaSubs[k]); } catch (e) { /* noop */ } });
      }
    } catch (e) { /* noop */ }
  }

  private unsubscribeSafe(sub: Subscription | null | undefined): void {
    if (sub && sub.unsubscribe) {
      try { sub.unsubscribe(); } catch (e) { /* noop */ }
    }
  }

  /** Observa mudanças no campo raiz "anonimo" do formulário e aplica/remove validators nos campos da vítima. */
  private inscreverAnonimo(): void {
    if (!this.formGroup) return;
    const anonCtrl = this.formGroup.get('anonimo');
    if (!anonCtrl || !('valueChanges' in anonCtrl)) return;

    const aplicarOuRemover = (isAnonimo: boolean) => {
      const assedioGroup = this.formGroup.get('denunciaAssedio') as FormGroup;
      if (!assedioGroup) return;
      const participantes = assedioGroup.get('participantes') as FormArray;
      if (!participantes || participantes.length === 0) return;

      const vitima = participantes.at(0) as FormGroup;
      if (!vitima) return;

      const camposVitima = ['nome', 'idade', 'escolaridadeId', 'nacionalidade', 'funcaoId', 'corRacaId', 'celular', 'email'];
      camposVitima.forEach(field => {
        const ctrl = vitima.get(field);
        if (!ctrl) return;
        if (isAnonimo) {
          this.removerValidators(ctrl);
        } else {
          this.aplicarValidators(ctrl, [Validators.required]);
        }
      });
    };

    // aplicar estado inicial conforme valor atual
    try { aplicarOuRemover(!!anonCtrl.value); } catch (e) { /* noop */ }

    // observar mudanças subsequentes
    this.unsubscribeSafe(this.anonimoSub);
    this.anonimoSub = (anonCtrl as any).valueChanges.subscribe((val: any) => aplicarOuRemover(!!val));
  }

  private unwrap(ret: unknown): unknown[] {
    if (!ret) return [];
    const result: any = ret;
    // tenta suportar vários formatos de retorno: array direto ou objeto com campos comuns
    if (Array.isArray(result)) return result;
    if (result.lista) return result.lista;
    if (result.retorno) return result.retorno;
    if (result.data) return result.data;
    return result;
  }

 private normalizarOpcoes(list: unknown[]): ListaOpcao[] {
    if (!list) return [];
    return list.map((item: any) => {
      if (item == null) return item;
      return {
        value: item.codigo || item.id || item.value || item.valor || item.valor || item.nome || item.nomeBusca || item.codigo,
        nome: item.nome || item.label || item.descricao || item.nomeBusca || item.valor || item.valor || item.codigo
      };
    });
  }

  private normalizarOpcoesSetor(list: unknown[]): ListaOpcao[] {
    if (!list) return [];
    return list.map((item: any) => {
      if (item == null) return item;
      const sigla = item.sigla || item.setorSigla || item.setor || item.codigo || item.value || item.valor || null;

      // determinar exibição do campo `nome` dependendo do valor atual de `area`:
      // - se a área for 1 (primeira instância), exibir `item.nome`
      // - caso contrário, priorizar `sigla` quando presente
      let areaVal: PrimitiveValue = null;
      try {
        const ctrl = this.getControl('denunciaAssedio.area');
        areaVal = ctrl ? ctrl.value : null;
      } catch (e) {
        areaVal = null;
      }
      const isAreaOne = (areaVal === '1' || areaVal === 1);

      const nome = isAreaOne
        ? (item.nome || item.label || item.descricao || item.setor || item.valor || item.codigo)
        : (sigla || item.nome || item.label || item.descricao || item.setor || item.valor || item.codigo);

      const value = sigla || item.codigo || item.id || item.value || item.valor || nome;

      return Object.assign({}, item, { value: String(value), nome: String(nome), sigla: sigla });
    });
  }

  private normalizarOpcoesSetorComarca(list: unknown[]): ListaOpcao[] {
    if (!list) return [];
    return list.map((item: any) => {
      if (item == null) return item;
      const value = item.sigla || item.setorSigla || item.setor || item.codigo || item.value || item.valor || item.nome;
      const nome = item.nome || item.label || item.descricao || value;

      return Object.assign({}, item, { value: String(value), nome: String(nome), sigla: item.sigla || item.setorSigla || null });
    });
  }

  private garantirSetorSelecionadoNaLista(list: ListaOpcao[], selected: PrimitiveValue): ListaOpcao[] {
    if (selected === null || selected === undefined || selected === '') return list || [];
    const value = String(selected);
    const exists = (list || []).some(item => {
      if (!item) return false;
      const candidates = [item.value, item.sigla, item.setorSigla, item.setor, item.codigo, item.id, item.nome, item.label, item.descricao, item.valor];
      return candidates.some(c => c !== undefined && c !== null && String(c).toLowerCase() === value.toLowerCase());
    });
    if (exists) return list || [];
    const selectedOption: ListaOpcao = { value: value, nome: value, sigla: value };
    return [selectedOption].concat(list || []);
  }

  /** Retorna o valor canônico (string) correspondente a `existing` dentro de `list`, ou null. */
  private findCanonicalSetorValue(list: ListaOpcao[], existing: PrimitiveValue): string | null {
    if (!list || !list.length) return null;
    if (existing === null || existing === undefined || existing === '') return null;
    const target = String(existing).toLowerCase();
    for (const it of list) {
      if (!it) continue;
      const candidates = [it.value, it.sigla, it.setorSigla, it.setor, it.id, it.codigo, it.nome, it.label, it.descricao, it.valor];
      for (const c of candidates) {
        if (c === undefined || c === null) continue;
        if (String(c).toLowerCase() === target) return String(it.value);
      }
    }
    return null;
  }

  /**
   * SetValue seguro e padronizado.
   * - emitEvent: controla emissão de eventos (default false)
   * - reapply: reaplica em macrotask para componentes que sincronizam internamente (default true)
   */
  private safeSetValue(
    ctrl: AbstractControl | null,
    value: unknown,
    options?: { emitEvent?: boolean; reapply?: boolean }
  ): void {
    if (!ctrl) return;

    const emitEvent = options && options.emitEvent === true;
    const reapply = options ? options.reapply !== false : true;

    this.trySetControlValue(ctrl, value, emitEvent);
    if (!reapply) return;

    this.runDeferred(() => this.trySetControlValue(ctrl, value, emitEvent), 'macro');
  }

  private limparCamposComarca(): void {
    this.safeSetValue(this.getControl('denunciaAssedio.comarcaMunicipioLabel'), null, { emitEvent: false, reapply: false });
    this.safeSetValue(this.getControl('denunciaAssedio.comarcaMunicipioCodigo'), null, { emitEvent: false, reapply: false });
  }

  private podeGerenciarSetor(): boolean {
    return !this.edicao || this.setorBackendCarregado;
  }

  private limparSetorSelecionado(emitEvent: boolean = false, reapply: boolean = true): void {
    const ctrl = this.getControl('denunciaAssedio.setor');
    if (!ctrl || !this.podeGerenciarSetor()) return;
    this.safeSetValue(ctrl, null, { emitEvent: emitEvent, reapply: reapply });
  }

  private limparEstadoAreaInvalida(): void {
    this.setores = [];
    // Preserva comportamento anterior do fluxo de área inválida (emitindo evento).
    this.limparSetorSelecionado(true, false);
  }

  private obterSetorAtualParaArea(): PrimitiveValue {
    const grpForSet = this.formGroup ? (this.formGroup.get('denunciaAssedio') as FormGroup) : null;
    return grpForSet ? (grpForSet.get('setor') ? grpForSet.get('setor')!.value : null) : null;
  }

  private aplicarSetoresPorArea(normalized: ListaOpcao[], existingSetorVal: PrimitiveValue): void {
    const ctrl = this.getControl('denunciaAssedio.setor');
    if (!ctrl) return;

    const existing = this.valorPreenchido(existingSetorVal) ? existingSetorVal : this.getSetorParaReaplicar();

    if (this.valorPreenchido(existing) && this.podeGerenciarSetor()) {
      this.setores = this.garantirSetorSelecionadoNaLista(normalized, existing);
      const canonical = this.findCanonicalSetorValue(normalized, existing);
      if (canonical) this.safeSetValue(ctrl, canonical);
      else this.safeSetValue(ctrl, existing);
      return;
    }

    this.setores = normalized;
    this.limparSetorSelecionado(false, true);
  }

  private carregarSetoresDaArea(identificador: PrimitiveValue, existingSetorVal: PrimitiveValue): void {
    this.denunciaService.carregarSetores(identificador).subscribe(ret => {
      const normalized = this.normalizarOpcoesSetor(this.unwrap(ret));
      this.aplicarSetoresPorArea(normalized, existingSetorVal);
    }, err => {
      this.setores = [];
      this.limparSetorSelecionado(false, true);
    });
  }

  onAreaSelect(identificador: PrimitiveValue) {

    // Ao selecionar área (2ª instância), comarca deixa de ser aplicável.
    // Limpar ambos os campos de comarca evita inconsistência entre UI e payload.
    this.limparCamposComarca();

    // ignorar chamadas sem parâmetro válido (p.ex. re-render inicial ao trocar instância)
    if (!this.valorPreenchido(identificador)) {
      this.limparEstadoAreaInvalida();
      return;
    }

    // capturar valor previamente selecionado (se houver) para reaplicar após popular a lista
    const existingSetorVal = this.obterSetorAtualParaArea();
    this.carregarSetoresDaArea(identificador, existingSetorVal);
  }

  /** Retorna true se a opção de setor selecionada representa 'Outro' (robusto: checa value e nome do item) */
  isSetorOutro(): boolean {
    try {
  const ctrl = this.getControl('denunciaAssedio.setor');
      if (!ctrl) return false;
      const val = ctrl.value;
      if (val === null || val === undefined || val === '') return false;
      const sval = val.toString().toLowerCase();
      if (sval.indexOf('outro') !== -1) return true;
      // procurar na lista de setores pelo value correspondente e inspecionar o nome
      const list = this.setores || [];
      for (const it of list) {
        const candidateVal = it && (it.value || it.id || it.codigo || it);
        if (candidateVal != null && candidateVal.toString() === val.toString()) {
          const nome = (it && (it.nome || it.label || it.descricao || it.valor)) || '';
          if (nome && nome.toString().toLowerCase().indexOf('outro') !== -1) return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  private _toggleAreaSetorRequired(val: PrimitiveValue) {
    const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
    if (!grp) return;
    const areaCtrl = grp.get('area');
    const setorCtrl = grp.get('setor');
    const comarcaCtrl = grp.get('comarcaMunicipioCodigo');
    const instancia = this.parseInstancia(val);

    // agendar alterações para o próximo macrotask para não alterar estado durante a checagem
    this.runDeferred(() => {
      if (instancia === this.enumInstancia.SEGUNDA) {
        this.aplicarObrigatoriedade(areaCtrl, true);
        this.aplicarObrigatoriedade(setorCtrl, true);
        this.aplicarObrigatoriedade(comarcaCtrl, false);
        return;
      }

      if (instancia === this.enumInstancia.PRIMEIRA) {
        this.aplicarObrigatoriedade(comarcaCtrl, true);
        this.aplicarObrigatoriedade(areaCtrl, false);
        this.aplicarObrigatoriedade(setorCtrl, false);
        return;
      }

      this.aplicarObrigatoriedade(areaCtrl, false);
      this.aplicarObrigatoriedade(setorCtrl, false);
    }, 'macro');
  }

  private aplicarObrigatoriedade(ctrl: AbstractControl | null, required: boolean): void {
    if (!ctrl) return;
    if (required) ctrl.setValidators([Validators.required]);
    else ctrl.clearValidators();
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  carregarPerguntas() {
    this.denunciaService.getPerguntas('ASSEDIO_MORAL_SEXUAL_DISCRIMINACAO').subscribe(ret => {
      // Atribuir o retorno cru (após unwrap) diretamente a perguntas para preservar
      // a estrutura original do JSON recebido pelo backend (id, codigo, ordem, opcoes, childPergunta etc.).
      const p = this.unwrap(ret) || [];
      // Normalizar as opções para garantir compatibilidade com os componentes Carbon
      (p || []).forEach((perg: any) => {
        if (perg.opcoes && Array.isArray(perg.opcoes)) {
          perg.opcoes = this.garantirFormatoOpcoes(perg.opcoes);
        }
        // childPergunta pode ser objeto ou array; normalizar se presente
        if (perg.opcoes && Array.isArray(perg.opcoes)) {
          perg.opcoes.forEach((opt: any) => {
            if (opt.childPergunta) {
              const childs = this.toArray(opt.childPergunta);
              childs.forEach((cp: any) => {
                if (cp.opcoes && Array.isArray(cp.opcoes)) {
                  cp.opcoes = this.garantirFormatoOpcoes(cp.opcoes);
                }
              });
            }
          });
        }
      });
      this.perguntas = p;
      // garantir que existam controles dinamicos para cada pergunta que usa formControlName (checkbox/radio/text)
      try {
  this.garantirControlesPerguntasExistem();
      } catch (e) { /* noop */ }
      // recalcular e atribuir ordens sequenciais considerando todas as perguntas e childPergunta
      try {
        this.assignOrdensToPerguntas();
        // após popular perguntas, reaplicar respostas do form (se houver)
        try { this.aplicarRespostasNasPerguntas(); } catch (e) { /* noop */ }
        // inscrever observadores para perguntas que possuem childPergunta de forma a detectar
        // transições de seleção e limpar filhos mesmo quando a alteração vier de outras rotas
        try { this.inscreverObservadoresPerguntasComFilhos(); } catch (e) { /* noop */ }
      } catch (e) { /* noop */ }
    });
  }

  /** Inscreve observadores em controles `pergunta_<id>` que tenham childPergunta para
   * detectar quando uma opção deixa de estar selecionada e então limpar os campos filhos.
   * Isso cobre mudanças vindas de componentes externos, reaplicação de respostas ou setValue
   * programáticos que não passam pelos handlers manuais.
   */
  private inscreverObservadoresPerguntasComFilhos(): void {
    try {
      const grp = this.obterGrupoDenuncia();
      if (!grp) return;
      const walk = (list: any[]) => {
        if (!list) return;
        list.forEach((p: any) => {
          if (!p || !p.opcoes || !p.opcoes.length) {
            // também verificar childPergunta direto na pergunta
            if (p && p.childPergunta) {
              const childs = Array.isArray(p.childPergunta) ? p.childPergunta : [p.childPergunta];
              walk(childs);
            }
            return;
          }
          // se alguma opção tem childPergunta, inscrever no controle desta pergunta
          const hasChild = p.opcoes.some((o: any) => o && (o.childPergunta));
          if (hasChild && p.id != null) {
            const key = `pergunta_${p.id}`;
            // evitar reinscrição
            if (this.perguntaSubs[key]) return;
            const ctrl = grp.get(key);
            if (!ctrl || !ctrl.valueChanges) return;
            // armazenar valor inicial
            try { this.perguntaPrevVals[key] = ctrl.value; } catch (e) { this.perguntaPrevVals[key] = null; }
            try {
              const sub = (ctrl.valueChanges as any).pipe(startWith(ctrl.value)).subscribe((val: any) => {
                try {
                  const prev = this.perguntaPrevVals[key];
                  const prevNorm = this.normalizePerguntaValor(prev);
                  const curNorm = this.normalizePerguntaValor(val);
                  // detectar removidos quando array
                  if (Array.isArray(curNorm)) {
                    const prevArr = Array.isArray(prevNorm) ? prevNorm : (prevNorm ? [prevNorm] : []);
                    const removed = (prevArr || []).filter((it: any) => (curNorm || []).indexOf(it) === -1);
                    if (removed && removed.length) {
                      // limpar filhos em macrotask para evitar problemas com change detection
                      this.runDeferred(() => this.limparFilhosPorRemocao(p, removed), 'macro');
                    }
                  } else {
                    // caso primitivo (radio): prev != cur indica troca - limpar child do prev
                    if (prevNorm !== undefined && prevNorm !== null && curNorm !== undefined && prevNorm !== curNorm) {
                      this.runDeferred(() => this.limparFilhosPorTrocaRadio(p, prevNorm), 'macro');
                    }
                  }
                  // atualizar snapshot
                  try { this.perguntaPrevVals[key] = val; } catch (e) { this.perguntaPrevVals[key] = null; }
                } catch (e) { /* noop */ }
              });
              this.perguntaSubs[key] = sub;
            } catch (e) { /* noop */ }
          }
          // recursão para childPergunta nas opções
          p.opcoes.forEach((o: any) => {
            if (o && o.childPergunta) {
              const childs = Array.isArray(o.childPergunta) ? o.childPergunta : [o.childPergunta];
              walk(childs);
            }
          });
          // se a própria pergunta tiver childPergunta direta
          if (p.childPergunta) walk(this.toArray(p.childPergunta));
        });
      };
      walk(this.perguntas || []);
    } catch (e) { /* noop */ }
  }

  /**
   * Garante que para cada pergunta (e childPergunta) exista um FormControl `pergunta_<id>`
   * dentro do grupo `denunciaAssedio`. Isso permite que elementos com `formControlName`
   * no template (ex: carbon-checkbox-group) encontrem o controle mesmo antes de haver
   * resposta pré-existente.
   */
  private garantirControlesPerguntasExistem(): void {
    try {
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      if (!grp) return;
      const ensureRec = (list: any[]) => {
        if (!list) return;
        list.forEach((p: any) => {
          if (!p || p.id == null) return;
          const key = `pergunta_${p.id}`;
          if (!grp.get(key)) {
            // criar controle vazio apropriado: array para CHECKBOX, primitivo para RADIO/TEXT
            const tipo = (p.tipo || '').toString().toUpperCase();
            if (tipo === 'CHECKBOX' || tipo === 'CHOICE_MULTI') {
              grp.addControl(key, new FormControl([]));
            } else {
              grp.addControl(key, new FormControl(null));
            }
          }
          // verificar opcoes childPergunta dentro de opcoes
          if (p.opcoes && Array.isArray(p.opcoes)) {
            p.opcoes.forEach((op: any) => {
              if (op && op.childPergunta) {
                const childs = Array.isArray(op.childPergunta) ? op.childPergunta : [op.childPergunta];
                ensureRec(childs);
              }
            });
          }
          // pergunta pode ter childPergunta direto
          if (p.childPergunta) {
            const childs = Array.isArray(p.childPergunta) ? p.childPergunta : [p.childPergunta];
            ensureRec(childs);
          }
        });
      };
      ensureRec(this.perguntas || []);
    } catch (e) { /* noop */ }
  }

  /**
   * Copia os campos respostaTexto, respostaChoice e respostaChoiceValor das respostas
   * presentes em `formGroup.value.denunciaAssedio.respostas` para os objetos em `this.perguntas`.
   */
  private aplicarRespostasNasPerguntas(): void {
    try {
      const respostas = (this.formGroup && this.formGroup.value && this.formGroup.value.denunciaAssedio && this.formGroup.value.denunciaAssedio.respostas) ? this.formGroup.value.denunciaAssedio.respostas : [];
      if (!respostas || !respostas.length) return;
      if (!this.perguntas || !this.perguntas.length) return;

  const mapa = this.construirMapaPerguntas(this.perguntas || []);

      respostas.forEach((r: any) => {
  try { this.aplicarRespostaAPergunta(r, mapa); } catch (e) { /* noop */ }
      });

        try { (this.perguntas || []).forEach((p: any) => this.sincronizarObrigatoriedadePergunta(p)); } catch (e) { /* noop */ }

      // snapshot (debug) - mantido compatível com comportamento anterior
      try {
  const grpFinal = this.obterGrupoDenuncia();
        if (grpFinal) {
          const keys = Object.keys(grpFinal.controls || {}).filter(k => k && k.indexOf('pergunta_') === 0);
          const snapshot: any = {};
          keys.forEach(k => { try { snapshot[k] = grpFinal.get(k)!.value; } catch (e) { snapshot[k] = 'ERR'; } });
        }
      } catch (e) { /* noop */ }

    } catch (e) {
      // noop
    }
  }

  // ----------------------- helpers para aplicar respostas -----------------------
  private obterGrupoDenuncia(): FormGroup | null {
    return this.formGroup ? (this.formGroup.get('denunciaAssedio') as FormGroup) : null;
  }

  private adicionarOuDefinirControle(grp: FormGroup, key: string, value: any): void {
    if (!grp) return;
    try {
      if (grp.get(key)) {
        grp.get(key)!.setValue(value, { emitEvent: false });
      } else {
        grp.addControl(key, new FormControl(value));
      }
    } catch (e) {
      try { if (grp.get(key)) grp.get(key)!.setValue(value); else grp.addControl(key, new FormControl(value)); } catch (ee) { /* noop */ }
    }
  }

  private construirMapaPerguntas(lista: any[]): Map<number, any> {
    const mapa = new Map<number, any>();
    const buildRec = (items: any[]) => {
      if (!items) return;
      items.forEach((q: any) => {
        if (!q || q.id == null) return;
        mapa.set(Number(q.id), q);
        if (q.opcoes && Array.isArray(q.opcoes)) {
          q.opcoes.forEach((op: any) => {
            if (op && op.childPergunta) {
              const childs = Array.isArray(op.childPergunta) ? op.childPergunta : [op.childPergunta];
              buildRec(childs);
            }
          });
        }
        if (q.childPergunta) {
          const childs = Array.isArray(q.childPergunta) ? q.childPergunta : [q.childPergunta];
          buildRec(childs);
        }
      });
    };
    buildRec(lista || []);
    return mapa;
  }

  private aplicarRespostaAPergunta(r: any, mapa: Map<number, any>): void {
    if (!r || r.perguntaId == null) return;
    const pergunta = mapa.get(Number(r.perguntaId));
    if (!pergunta) return;
    const tipo = (pergunta.tipo || '').toString().toUpperCase();
  const grp = this.obterGrupoDenuncia();
    const key = `pergunta_${pergunta.id}`;

    if (tipo === 'TEXT') {
      if (r.respostaTexto !== undefined && r.respostaTexto !== null) pergunta.respostaTexto = r.respostaTexto;
      const valToSet = String(pergunta.respostaTexto || '');
  if (grp) this.adicionarOuDefinirControle(grp, key, valToSet);
      this.selectedOptions[`p_${pergunta.id}`] = valToSet;
      return;
    }

    if (tipo === 'RADIO' || tipo === 'CHOICE') {
      if (r.respostaChoice !== undefined && r.respostaChoice !== null) pergunta.respostaChoice = r.respostaChoice;
      if (r.respostaChoiceValor !== undefined && r.respostaChoiceValor !== null) pergunta.respostaChoiceValor = r.respostaChoiceValor;
      const val = pergunta.respostaChoice !== undefined && pergunta.respostaChoice !== null ? String(pergunta.respostaChoice) : (pergunta.respostaChoiceValor !== undefined && pergunta.respostaChoiceValor !== null ? String(pergunta.respostaChoiceValor) : null);
  if (grp) this.adicionarOuDefinirControle(grp, key, val);
      this.selectedOptions[`p_${pergunta.id}`] = val;
      return;
    }

    if (tipo === 'CHECKBOX' || tipo === 'CHOICE_MULTI') {
      const rawChoice = (r.respostaChoice !== undefined && r.respostaChoice !== null) ? String(r.respostaChoice) : '';
      pergunta.respostaChoice = rawChoice === '' ? [] : rawChoice.split(',').map((s: string) => s.trim()).filter((s: string) => s !== '');
      const rawValor = (r.respostaChoiceValor !== undefined && r.respostaChoiceValor !== null) ? String(r.respostaChoiceValor) : '';
      pergunta.respostaChoiceValor = rawValor === '' ? [] : rawValor.split(',').map((s: string) => s.trim()).filter((s: string) => s !== '');
      if (r.respostaTexto !== undefined && r.respostaTexto !== null) pergunta.respostaTexto = r.respostaTexto;
      const arrVals = (pergunta.respostaChoice || []).map((v: any) => String(v));
  if (grp) this.adicionarOuDefinirControle(grp, key, arrVals);
      this.selectedOptions[`p_${pergunta.id}`] = arrVals;
      return;
    }

    // fallback: manter campos presentes
    if (r.respostaTexto !== undefined) pergunta.respostaTexto = r.respostaTexto;
    if (r.respostaChoice !== undefined) pergunta.respostaChoice = r.respostaChoice;
    if (r.respostaChoiceValor !== undefined) pergunta.respostaChoiceValor = r.respostaChoiceValor;
  }

  /** Normaliza o controle 'instancia' para string quando necessário (ex: recebe 1 ao invés de '1'). */
  private normalizarControleInstancia() {
    try {
      const grpForInst = this.formGroup ? (this.formGroup.get('denunciaAssedio') as FormGroup) : null;
      const instCtrlInit = grpForInst ? grpForInst.get('instancia') : null;
      if (instCtrlInit && instCtrlInit.value !== null && instCtrlInit.value !== undefined && instCtrlInit.value !== '') {
        const v = instCtrlInit.value;
        const asStr = typeof v === 'number' ? String(v) : (typeof v === 'string' ? v : String(v));
        try { instCtrlInit.setValue(asStr, { emitEvent: false }); } catch (e) { /* noop */ }
      }
    } catch (e) { /* noop */ }
  }

  /**
   * Atribui números sequenciais de ordem a `this.perguntas` incluindo quaisquer childPergunta
   * mesmo que estejam ocultas no momento. A ordem é calculada em profundidade por aparição
   * na árvore: pergunta principal -> childPergunta de cada opção (na ordem das opções) -> recursivamente.
   * Após atribuir ordens, notifica o DenunciaService para atualizar seu cache `perguntaById`.
   */
  assignOrdensToPerguntas() {
    let counter = 0;

    const assignRec = (list: any[]) => {
      if (!list) return;
      list.forEach((perg: any) => {
        if (!perg) return;
        counter++;
        // garantir que perg.ordem seja número sequencial
        perg.ordem = counter;

        // se a pergunta possuir opções, verificar childPergunta em cada opção
        if (perg.opcoes && Array.isArray(perg.opcoes)) {
          perg.opcoes.forEach((opt: any) => {
            const childs = this.toArray(opt.childPergunta);
            if (childs && childs.length) {
              // cada childPergunta deve ser tratada como nova pergunta na sequência
              assignRec(childs);
            }
          });
        }

        // também, caso a própria pergunta tenha childPergunta direta (por esquema diferente)
        if (perg.childPergunta) {
          assignRec(this.toArray(perg.childPergunta));
        }
      });
    };

    assignRec(this.perguntas || []);

    try {
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      if (grp) {
        const ordemMap: { [id: string]: number } = {};
        const collect = (list: any[]) => {
          if (!list) return;
          list.forEach((p: any) => {
            if (!p) return;
            if (p.id != null) ordemMap[String(p.id)] = Number(p.ordem);
            if (p.opcoes && Array.isArray(p.opcoes)) {
              p.opcoes.forEach((opt: any) => {
                const childs = this.toArray(opt.childPergunta);
                if (childs && childs.length) collect(childs);
              });
            }
            if (p.childPergunta) collect(this.toArray(p.childPergunta));
          });
        };
        collect(this.perguntas || []);
        // adicionar/atualizar controle perguntasOrdens
        try {
          const ctrl = grp.get('perguntasOrdens');
          if (ctrl) {
            ctrl.setValue(ordemMap);
          } else {
            grp.addControl('perguntasOrdens', new FormControl(ordemMap));
          }
        } catch (e) { /* noop */ }
      }
    } catch (e) { /* noop */ }
  }

  /** Garante que cada opção tenha as chaves `val` e `label` (padrão do componente carbon). */
  private garantirFormatoOpcoes(options: any[]): any[] {
    return (options || []).map((opt: any) => {
      if (!opt) return opt;
      const rawVal = opt.val !== undefined ? opt.val : (opt.value !== undefined ? opt.value : (opt.id !== undefined ? opt.id : opt.valor));
      const rawLabel = opt.label !== undefined ? opt.label : (opt.nome !== undefined ? opt.nome : (opt.descricao !== undefined ? opt.descricao : opt.valor));
      // coercionar val para string quando não-nulo para evitar mismatch de tipos (comparação estrita no componente)
      const val = (rawVal === undefined || rawVal === null) ? rawVal : String(rawVal);
      const label = (rawLabel === undefined || rawLabel === null) ? rawLabel : String(rawLabel);
      // Retornar um novo objeto mantendo propriedades originais, mas garantindo val/label como strings
      return Object.assign({}, opt, { val: val, label: label });
    });
  }

  /**
   * Utility: garante que o valor retornado seja um array (para uso no template).
   * - Se value for array, retorna value
   * - Se value for nulo/undefined, retorna []
   * - Caso contrário, retorna [value]
   */
  toArray(value: any): any[] {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  getControl(path: string) {
    if (!this.formGroup) return null;
    return this.formGroup.get(path as any);
  }

  /**
   * Retorna true se o controle do participante indicado (index) no campo informado
   * tem valor 'Outro' (por exemplo value === 9999) ou se a opção selecionada na lista
   * possui nome contendo 'outro'. Usado para mostrar campos "Especifique".
   */
  isParticipantFieldOutro(index: number, field: string, optionsList: any[]): boolean {
    try {
      const ctrl = this.getParticipantControl(index, field);
      if (!ctrl) return false;
      const val = ctrl.value;
      if (val === null || val === undefined || val === '') return false;
      // checagem explícita por id reservado
      if (Number(val) === 9999) return true;
      // procurar na lista pelo valor e inspecionar o nome/label
      const list = optionsList || [];
      for (const it of list) {
        const candidateVal = it && (it.value || it.id || it.codigo || it);
        if (candidateVal != null && candidateVal.toString() === val.toString()) {
          const nome = (it && (it.nome || it.label || it.descricao || it.valor)) || '';
          if (nome && nome.toString().toLowerCase().indexOf('outro') !== -1) return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  // manipulação simples para condutas (checkboxes)
  isCondutaSelected(item: any): boolean {
    const ctrl = this.getControl('denunciaAssedio.condutas');
    if (!ctrl) return false;
    const arr = ctrl.value || [];
    return arr.some((i: any) => i && (i.id === item.id || i.value === item.value || i.nome === item.nome));
  }

  toggleConduta(item: any) {
    const ctrl = this.getControl('denunciaAssedio.condutas');
    if (!ctrl) return;
    const arr = ctrl.value || [];
    const idx = arr.findIndex((i: any) => i && (i.id === item.id || i.value === item.value || i.nome === item.nome));
    if (idx >= 0) {
      arr.splice(idx, 1);
    } else {
      arr.push(item);
    }
    ctrl.setValue(arr);
  }

  private atualizarRespostaNoArray(grp: FormGroup, perguntaId: any, value: any, pergunta?: any): void {
    try {
      const respostasCtrl = grp.get('respostas');
      if (!respostasCtrl) return;

      const firstPresent = (...items: any[]) => {
        for (const item of items) {
          if (item !== undefined && item !== null && item !== '') return item;
        }
        return null;
      };

      const respostas = Array.isArray(respostasCtrl.value) ? respostasCtrl.value.slice() : [];
      const idx = respostas.findIndex((r: any) => r && String(r.perguntaId) === String(perguntaId));
      const perguntaText = pergunta ? firstPresent(pergunta.perguntaText, pergunta.pergunta, pergunta.label) : null;
      const base = idx >= 0 ? Object.assign({}, respostas[idx]) : {};
      const resposta: any = Object.assign(base, {
        perguntaId: Number(perguntaId),
        perguntaCodigo: firstPresent(base.perguntaCodigo, pergunta ? pergunta.codigo : null),
        perguntaText: firstPresent(perguntaText, base.perguntaText),
        respostaBoolean: null,
        respostaTexto: null,
        respostaChoice: null,
        respostaTipo: null,
        ordem: firstPresent(base.ordem, pergunta ? pergunta.ordem : null)
      });

      if (Array.isArray(value)) {
        const labels: string[] = [];
        const values: string[] = [];
        value.forEach((item: any) => {
          if (!item) return;
          labels.push(String(firstPresent(item.label, item.nome, item.texto, item.value, item.val, item.id, item.valor, item)));
          values.push(String(firstPresent(item.value, item.val, item.id, item.valor, item)));
        });
        resposta.respostaTexto = labels.join(',');
        resposta.respostaChoice = values.join(',');
        resposta.respostaTipo = 'CHOICE_MULTI';
      } else if (value && typeof value === 'object' && value.tipo === 'TEXT') {
        resposta.respostaTexto = value.texto != null ? String(value.texto) : null;
        resposta.respostaTipo = 'TEXT';
      } else if (value && typeof value === 'object') {
        resposta.respostaTexto = firstPresent(value.label, value.nome);
        resposta.respostaChoice = firstPresent(value.value, value.val, value.id, value.valor);
        resposta.respostaTipo = 'CHOICE';
      } else if (value !== undefined && value !== null && value !== '') {
        resposta.respostaTexto = String(value);
        resposta.respostaTipo = 'TEXT';
      }

      const hasTexto = resposta.respostaTexto != null && String(resposta.respostaTexto).trim() !== '';
      const hasChoice = resposta.respostaChoice != null && String(resposta.respostaChoice).trim() !== '';
      const hasBoolean = resposta.respostaBoolean != null;

      if (hasTexto || hasChoice || hasBoolean) {
        if (idx >= 0) respostas[idx] = resposta;
        else respostas.push(resposta);
      } else if (idx >= 0) {
        respostas.splice(idx, 1);
      }

      respostasCtrl.setValue(respostas, { emitEvent: false });
    } catch (e) { /* noop */ }
  }

  // helpers para perguntas dinâmicas: registra resposta em formGroup
  setRespostaPergunta(perguntaId: any, value: any, pergunta?: any) {
    const grp = this.formGroup && this.formGroup.get('denunciaAssedio') as FormGroup;
    if (!grp) return;
    const key = `pergunta_${perguntaId}`;

  // localizar definição da pergunta para extrair texto/labels, preferindo o objeto passado pelo template
  const perguntaObj = pergunta || (this.perguntas || []).find((p: any) => String(p.id) === String(perguntaId));
  const perguntaText = perguntaObj ? (perguntaObj.perguntaText || perguntaObj.pergunta || perguntaObj.label || null) : null;

    let toStore: any = null;
    let controlValue: any = value;

    try {
      // Caso: array (checkboxes emit array de valores/opções)
  if (Array.isArray(value)) {
          controlValue = (value || []).map(v => {
            if (v && typeof v === 'object') {
              if (v.value !== undefined && v.value !== null) return v.value;
              if (v.val !== undefined && v.val !== null) return v.val;
              if (v.id !== undefined && v.id !== null) return v.id;
              if (v.valor !== undefined && v.valor !== null) return v.valor;
            }
            return v;
          });
          toStore = (value || []).map(v => {
          // se já for objeto com label/value, manter e garantir perguntaText
          if (v && typeof v === 'object') {
            return Object.assign({}, v, { perguntaText: v.perguntaText || perguntaText });
          }
          // procurar opção correspondente na pergunta
          const found = pergunta && pergunta.opcoes ? (pergunta.opcoes.find((o: any) => String(o.val || o.value || o.id || o.valor) === String(v))) : null;
          const label = found ? (found.label || found.nome || String(found)) : String(v);
          const val = found ? (found.val || found.value || found.id || found.valor) : v;
          return { value: val, label: label, perguntaText };
        });
      } else if (value && typeof value === 'object') {
        // objeto individual retornado pelo componente
        toStore = Object.assign({}, value, { perguntaText: value.perguntaText || perguntaText });
        if ((value as any).tipo === 'TEXT') {
          controlValue = (value as any).texto !== undefined && (value as any).texto !== null ? (value as any).texto : '';
        } else if ((value as any).value !== undefined && (value as any).value !== null) {
          controlValue = (value as any).value;
        } else if ((value as any).val !== undefined && (value as any).val !== null) {
          controlValue = (value as any).val;
        } else if ((value as any).id !== undefined && (value as any).id !== null) {
          controlValue = (value as any).id;
        } else if ((value as any).valor !== undefined && (value as any).valor !== null) {
          controlValue = (value as any).valor;
        }
      } else {
        // primitivo: pode ser texto livre (input) ou opção de radio
        const primitive = (value !== undefined && value !== null) ? String(value) : '';
        controlValue = primitive;
        if (pergunta && pergunta.opcoes && pergunta.opcoes.length) {
          const found = pergunta.opcoes.find((o: any) => String(o.val || o.value || o.id || o.valor) === String(primitive));
          if (found) {
            toStore = { value: found.val || found.value || found.id || found.valor, label: found.label || found.nome || String(found), perguntaText: perguntaText || (found.label || found.nome) };
          } else {
            // não encontrou: armazenar como texto
            toStore = { tipo: 'TEXT', texto: primitive, perguntaText };
          }
        } else {
          // sem opções: é texto livre
          toStore = { tipo: 'TEXT', texto: primitive, perguntaText };
        }
      }
    } catch (e) {
      // fallback simples
      toStore = value;
    }

    const ctrl = grp.get(key);
    if (ctrl) {
      ctrl.setValue(controlValue, { emitEvent: false });
    } else {
      // criação dinâmica dentro do FormGroup denunciaAssedio
      try { grp.addControl(key, new FormControl(controlValue)); } catch (e) { /* noop */ }
    }
    this.atualizarRespostaNoArray(grp, perguntaId, toStore, perguntaObj);
  }

  /** Handler específico para mudanças em radios: atualiza mapa local e grava no form. */
  onRadioPerguntaChange(perguntaId: any, value: any, opc?: any, pergunta?: any) {
    // limpar filhos da opção previamente selecionada (se houver)
    try {
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      if (grp) {
        const key = `pergunta_${perguntaId}`;
        const ctrl = grp.get(key);
        const prev = ctrl ? ctrl.value : null;
        if (prev !== null && prev !== undefined && prev !== '' && prev !== value) {
          // encontrar option correspondente ao prev e limpar seus childPergunta
          const perguntaObj = pergunta || (this.perguntas || []).find((p: any) => String(p.id) === String(perguntaId));
          if (perguntaObj && perguntaObj.opcoes && perguntaObj.opcoes.length) {
            const prevOpt = perguntaObj.opcoes.find((o: any) => {
              try { return String(o.val || o.value || o.id || o.valor) === String(prev); } catch (e) { return false; }
            });
            if (prevOpt) this.clearChildResponses(perguntaObj, prevOpt);
          }
        }
      }
    } catch (e) { /* noop */ }

    // guardar no mapa local para disparo imediato da exibição de filhos
    this.selectedOptions[`p_${perguntaId}`] = value;
    this.sincronizarObrigatoriedadePergunta(pergunta || (this.perguntas || []).find((p: any) => String(p.id) === String(perguntaId)));
    // O componente Carbon também pode emitir valueChange durante a montagem inicial.
    // Adiar a gravação evita alterar a validade do form no ciclo da view pai.
    setTimeout(() => this.setRespostaPergunta(perguntaId, value, pergunta), 0);
  }

  onCheckboxPerguntaChange(perguntaId: any, opcao: any, checked: boolean, pergunta?: any) {
    const grp = this.formGroup && this.formGroup.get('denunciaAssedio') as FormGroup;
    if (!grp) return;
    const key = `pergunta_${perguntaId}`;

    // Caso 1: o componente carbon-checkbox-group emite um array de valores selecionados
    if (Array.isArray(opcao)) {
      // mapear array de valores/objetos para array de objetos enriquecidos (label + value + perguntaText)
      const perguntaObj = pergunta || (this.perguntas || []).find((p: any) => String(p.id) === String(perguntaId));
      const perguntaText = perguntaObj ? (perguntaObj.perguntaText || perguntaObj.pergunta || perguntaObj.label || null) : null;
      // mapear para um array de valores primitivos (o componente checkbox trabalha melhor com primitivos)
      const primVals = (opcao || []).map((v: any) => {
        if (v && typeof v === 'object') {
          return v.val !== undefined && v.val !== null ? v.val : (v.value !== undefined && v.value !== null ? v.value : (v.id !== undefined && v.id !== null ? v.id : (v.valor !== undefined && v.valor !== null ? v.valor : String(v))));
        }
        // primitivo
        return v;
      });
      let ctrl = grp.get(key);
      if (!ctrl) { grp.addControl(key, new FormControl([])); ctrl = grp.get(key); }
      if (ctrl) { ctrl.setValue(primVals); }
      this.atualizarRespostaNoArray(grp, perguntaId, primVals, perguntaObj);
      // recalcular mapa local a partir do valor do controle (fonte da verdade)
      try {
  const mapKey = `p_${perguntaId}`;
  const prev = this.selectedOptions[mapKey] || [];
  const stored = ctrl ? (ctrl.value || []) : [];
        const normalize = (it: any) => {
          if (it === undefined || it === null) return it;
          if (typeof it === 'string' || typeof it === 'number' || typeof it === 'boolean') return String(it);
          // itens mapeados aqui costumam ter a propriedade `value`
          const cand = it.value !== undefined && it.value !== null ? it.value : (it.val !== undefined && it.val !== null ? it.val : (it.id !== undefined && it.id !== null ? it.id : (it.valor !== undefined && it.valor !== null ? it.valor : (it.nome !== undefined && it.nome !== null ? it.nome : JSON.stringify(it)))));
          return cand === undefined || cand === null ? String(it) : String(cand);
        };
        this.selectedOptions[mapKey] = (stored || []).map((s: any) => normalize(s));
        // detectar removidos (prev - current) e limpar childPergunta correspondentes
        try {
          const removed = (prev || []).filter((pv: any) => (this.selectedOptions[mapKey] || []).indexOf(pv) === -1);
          if (removed && removed.length && pergunta && pergunta.opcoes && pergunta.opcoes.length) {
            removed.forEach((rv: any) => {
              const opt = pergunta.opcoes.find((o: any) => {
                try { return String(o.val || o.value || o.id || o.valor) === String(rv); } catch (e) { return false; }
              });
              if (opt) this.clearChildResponses(pergunta, opt);
            });
          }
        } catch (e) { /* noop */ }
      } catch (e) { /* noop */ }
      return;
    }

    // Caso 2: chamada com opcao individual + checked boolean (fallback)
    let ctrl = grp.get(key);
    if (!ctrl) { grp.addControl(key, new FormControl([])); ctrl = grp.get(key); }
    if (!ctrl) return;

    // normalizar o item individual para o mesmo formato usado na rota de array
    const perguntaObj = pergunta || (this.perguntas || []).find((p: any) => String(p.id) === String(perguntaId));
    const perguntaText = perguntaObj ? (perguntaObj.perguntaText || perguntaObj.pergunta || perguntaObj.label || null) : null;
    const normalizeItemToVal = (v: any) => {
      if (v && typeof v === 'object') return v.val !== undefined && v.val !== null ? v.val : (v.value !== undefined && v.value !== null ? v.value : (v.id !== undefined && v.id !== null ? v.id : (v.valor !== undefined && v.valor !== null ? v.valor : String(v))));
      return v;
    };

    const mappedVal = normalizeItemToVal(opcao);
    const arr = (ctrl.value || []);
    const idx = arr.findIndex((a: any) => {
      try { return String(a) === String(mappedVal); } catch (e) { return false; }
    });
    if (checked && idx === -1) { arr.push(mappedVal); } else if (!checked && idx >= 0) { arr.splice(idx, 1); }
    ctrl.setValue(arr);
    this.atualizarRespostaNoArray(grp, perguntaId, arr, perguntaObj);
    this.sincronizarObrigatoriedadePergunta(perguntaObj);
    // recalcular mapa local a partir do valor atual do controle (fonte da verdade)
    try {
      const mapKey = `p_${perguntaId}`;
      const stored = ctrl ? (ctrl.value || []) : [];
      const normalize = (it: any) => {
        if (it === undefined || it === null) return it;
        if (typeof it === 'string' || typeof it === 'number' || typeof it === 'boolean') return String(it);
        const cand = it.value !== undefined && it.value !== null ? it.value : (it.val !== undefined && it.val !== null ? it.val : (it.id !== undefined && it.id !== null ? it.id : (it.valor !== undefined && it.valor !== null ? it.valor : (it.nome !== undefined && it.nome !== null ? it.nome : JSON.stringify(it)))));
        return cand === undefined || cand === null ? String(it) : String(cand);
      };
      this.selectedOptions[mapKey] = (stored || []).map((s: any) => normalize(s));
      // se a ação foi um uncheck (checked === false), limpar childPergunta do item removido
      try {
        if (!checked) {
          const removedVal = normalizeItemToVal(opcao);
          const opt = pergunta && pergunta.opcoes ? pergunta.opcoes.find((o: any) => {
            try { return String(o.val || o.value || o.id || o.valor) === String(removedVal); } catch (e) { return false; }
          }) : null;
          if (opt) this.clearChildResponses(pergunta, opt);
        }
      } catch (e) { /* noop */ }
    } catch (e) { /* noop */ }
  }

  /** Handler para o input de contato do participante 1.
   * - Se o texto digitado parecer um e-mail, grava em participant[1].email
   * - Caso contrário, grava em participant[1].celular (se houver) ou participant[1].telefone
   */
  onContatoInput(val: string) {
    try {
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      if (!grp) return;
      const parts = grp.get('participantes') as FormArray;
      if (!parts || parts.length <= 1) return;
      const p1 = parts.at(1) as FormGroup;
      if (!p1) return;

      const contatoCtrl = p1.get('contato');
      if (contatoCtrl) contatoCtrl.setValue(val);

      // decidir se é email ou telefone (simples heurística)
      if (this.isEmail(val)) {
        const emailCtrl = p1.get('email');
        if (emailCtrl) {
          emailCtrl.setValue(val);
        } else {
          p1.addControl('email', new FormControl(val));
        }
        // limpar telefone se havia
        const telCtrl = p1.get('celular') || p1.get('telefone');
        if (telCtrl) telCtrl.setValue(null);
      } else {
        // gravar em celular preferencialmente
        const celularCtrl = p1.get('celular');
        if (celularCtrl) {
          celularCtrl.setValue(val);
        } else {
          const tel = p1.get('telefone');
          if (tel) {
            tel.setValue(val);
          } else {
            p1.addControl('celular', new FormControl(val));
          }
        }
        // limpar email se havia
        const emailCtrl2 = p1.get('email');
        if (emailCtrl2) emailCtrl2.setValue(null);
      }
    } catch (e) {
      // noop
    }
  }

  /** Atualiza o controle `aceiteTermo` no grupo denunciaAssedio a partir de um array
   * emitido pelo componente carbon-checkbox-group (opções selecionadas).
   * Se a opção 'S' estiver presente, definimos 'S', caso contrário null.
   */
  setAceiteTermo(selected: any[]) {
    try {
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);
      if (!grp) return;
      const ctrl = grp.get('aceiteTermo');
      if (!ctrl) return;
      if (Array.isArray(selected) && selected.findIndex(s => String(s) === 'S') >= 0) {
        ctrl.setValue('S');
      } else {
        ctrl.setValue(null);
      }
    } catch (e) { /* noop */ }
  }

  private isEmail(val: string): boolean {
    if (!val || typeof val !== 'string') return false;
    // verificação simples: contém '@' e um '.' depois do '@'
    const v = val.trim();
    const at = v.indexOf('@');
    if (at <= 0) return false;
    const dot = v.indexOf('.', at);
    return dot > at + 1 && dot < v.length - 1;
  }

  /**
   * Retorna true se a opção `opcao` da pergunta `p` estiver selecionada no form.
   * - Para RADIO: compara valor/string
   * - Para CHECKBOX: verifica se o array de respostas contém a opção
   */
  isOptionSelected(p: any, opcao: any): boolean {
    try {
      const grp = this.formGroup && (this.formGroup.get('denunciaAssedio') as FormGroup);

      const matchesOption = (item: any, option: any): boolean => {
        if (item === undefined || item === null) return false;
        // candidates from option
        const optCandidates = [option && option.val, option && option.value, option && option.id, option && option.valor, option && option.nome, option && option.label];
        // primitive item
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
          const s = String(item);
          return optCandidates.some(c => c !== undefined && c !== null && String(c) === s);
        }
        // object item: compare common fields both ways
        const itemCandidates = [item.val, item.value, item.id, item.valor, item.nome, item.label];
        for (const ic of itemCandidates) {
          if (ic === undefined || ic === null) continue;
          for (const oc of optCandidates) {
            if (oc === undefined || oc === null) continue;
            if (String(ic) === String(oc)) return true;
          }
        }
        // direct equality fallback
        try { if (item === option || JSON.stringify(item) === JSON.stringify(option)) return true; } catch (e) { /* noop */ }
        return false;
      };

      // preferir o valor atual do controle (fonte da verdade)
      if (grp) {
        const key = `pergunta_${p.id}`;
        const ctrl = grp.get(key);
        if (ctrl) {
          const val = ctrl.value;
          if (Array.isArray(val)) {
            return val.some((v: any) => matchesOption(v, opcao));
          }
          if (val && typeof val === 'object') {
            return matchesOption(val, opcao);
          }
          if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
            return matchesOption(val, opcao);
          }
        }
      }

      // fallback: mapa local usado para atualização imediata da UI
      const mapKey = `p_${p.id}`;
      const mapped = this.selectedOptions[mapKey];
      if (mapped !== undefined && mapped !== null) {
        if (Array.isArray(mapped)) {
          return mapped.some((mv: any) => matchesOption(mv, opcao));
        }
        return matchesOption(mapped, opcao);
      }

      return false;
    } catch (e) {
      return false;
    }
  }

}
